// Email Layout Templates for CRM Email Campaigns

import { toAbsoluteUrl } from './public-url'

export type EmailLayoutType = 'olive_green_newsletter' | 'simple_text' | 'webinar' | 'case_study' | 'ceo_letter' | 'masthead_newsletter' | 'brochure' | 'use_case_brochure' | 'spotlight_webinar'

export interface EmailLayoutTemplate {
  id: EmailLayoutType
  name: string
  description: string
  defaultSubject: string
  defaultContent: string
  style: {
    backgroundColor: string
    primaryColor: string
    secondaryColor: string
    textColor: string
    headerStyle: string
  }
}

export const EMAIL_LAYOUTS: Record<EmailLayoutType, EmailLayoutTemplate> = {
  olive_green_newsletter: {
    id: 'olive_green_newsletter',
    name: 'Newsletter',
    description: 'Professional newsletter template with customer case study and company news sections',
    defaultSubject: 'XOP Connect - Newsletter - {{TENANT_NAME}} - {{MONTH}} {{YEAR}}',
    defaultContent: ' ',
    style: {
      backgroundColor: '#f5f5dc',
      primaryColor: '#6B8E23',
      secondaryColor: '#8FBC8F',
      textColor: '#2d3748',
      headerStyle: 'olive_green'
    }
  },
  simple_text: {
    id: 'simple_text',
    name: 'Simple Text Email',
    description: 'Clean, straightforward text-based email template',
    defaultSubject: 'Message from {{TENANT_NAME}}',
    // Body fragment ONLY — the logo header, "Hello <name>," greeting and footer
    // are added by crm-api. No greeting here (it would double up).
    defaultContent: '<p>Write your message here...</p>',
    style: {
      backgroundColor: '#ffffff',
      primaryColor: '#3182ce',
      secondaryColor: '#60a5fa',
      textColor: '#1f2937',
      headerStyle: 'simple'
    }
  },
  ceo_letter: {
    id: 'ceo_letter',
    name: 'CEO Letter',
    description: 'A minimal, personal note — no logo banner, no footer. Just a "Hi <name>," greeting and your message, with inline images.',
    defaultSubject: 'A personal note from {{TENANT_NAME}}',
    // Body fragment ONLY. crm-api prepends the personalized "Hi <first name>,"
    // greeting from the recipient contact, so do NOT write a greeting here.
    defaultContent: '<p>I wanted to personally reach out and share something I think you will find valuable.</p><p>Write your message here...</p>',
    style: {
      backgroundColor: '#ffffff',
      primaryColor: '#1a202c',
      secondaryColor: '#4a5568',
      textColor: '#1a202c',
      headerStyle: 'simple'
    }
  },
  webinar: {
    id: 'webinar',
    name: 'Webinar Invitation',
    description: 'Professional webinar invitation template with event details and description',
    defaultSubject: 'Webinar Invitation from {{TENANT_NAME}}',
    defaultContent: ' ',
    style: {
      backgroundColor: '#f8fafc',
      primaryColor: '#2563eb',
      secondaryColor: '#3b82f6',
      textColor: '#1e293b',
      headerStyle: 'webinar'
    }
  },
  masthead_newsletter: {
    // NB: the `id` stays `masthead_newsletter` (a persisted `layout` enum value on
    // saved campaigns + the crm-api allowlist) even though the user-facing name is
    // now "Masthead Webinar Invite" — renaming the id would orphan existing sends.
    id: 'masthead_newsletter',
    name: 'Masthead Webinar Invite',
    description: 'Trade-publication style webinar invite: branded masthead, title bar, hero with sponsor lockup, date + register block, body, and a dark footer',
    defaultSubject: "You're invited: {{TENANT_NAME}} webinar",
    // Section-built (like the newsletter/webinar) — the builder emits the
    // complete email HTML document, which crm-api passes through verbatim.
    defaultContent: ' ',
    style: {
      backgroundColor: '#e9ebef',
      primaryColor: '#2a3e6a',
      secondaryColor: '#acce3e',
      textColor: '#1a202c',
      headerStyle: 'masthead'
    }
  },
  case_study: {
    id: 'case_study',
    name: 'Case Study',
    description: 'Customer success story: title image, customer, problem, XOP solution, network diagram, benefits',
    defaultSubject: 'Case Study: How {{TENANT_NAME}} helped a customer succeed',
    // Section-built (like the newsletter) — the builder generates the HTML.
    defaultContent: ' ',
    style: {
      backgroundColor: '#ffffff',
      primaryColor: '#6B8E23',
      secondaryColor: '#8FBC8F',
      textColor: '#2d3748',
      headerStyle: 'olive_green'
    }
  },
  brochure: {
    id: 'brochure',
    name: 'Brochure',
    description: 'A modern, illustration-forward brochure: bold navy hero, emoji feature highlights, an image showcase, and a call-to-action — built table-based with bulletproof buttons so it renders across every email client.',
    defaultSubject: 'Discover what {{TENANT_NAME}} can do for you',
    // Section-built (like the webinar) — the builder generates the complete HTML.
    defaultContent: ' ',
    style: {
      backgroundColor: '#F5F7FB',
      primaryColor: '#16233D',
      secondaryColor: '#B4D335',
      textColor: '#1F2937',
      headerStyle: 'brochure'
    }
  },
  use_case_brochure: {
    id: 'use_case_brochure',
    name: 'Use-Case Brochure',
    description: 'An editorial, photo-led problem→solution one-pager: a single photo hero with one title, a balanced intro, a two-column "what you need vs. the old way" comparison, and a two-step solution flow. Clean, single-focus layout; renders across every email client.',
    defaultSubject: 'How {{TENANT_NAME}} solves it',
    // Section-built (like the brochure/webinar) — the builder generates the complete HTML.
    defaultContent: ' ',
    style: {
      backgroundColor: '#F5F7FB',
      primaryColor: '#16233D',
      secondaryColor: '#B4D335',
      textColor: '#1F2937',
      headerStyle: 'brochure'
    }
  },
  spotlight_webinar: {
    id: 'spotlight_webinar',
    name: 'Spotlight Webinar Invite',
    description: 'The house webinar invite: logo, full-width banner, "joint webinar | date" line, two-part title, Register Now, a personal "Hi <first name>," intro, the topics you will cover, When?, and round speaker headshots. Change the images and colours freely — the layout stays the same.',
    defaultSubject: "Webinar: {{TENANT_NAME}} invites you",
    // Section-built — the builder emits the complete HTML document.
    defaultContent: ' ',
    style: {
      backgroundColor: '#eef0f4',
      primaryColor: '#2a3e6a',
      secondaryColor: '#acce3e',
      textColor: '#3f4859',
      headerStyle: 'spotlight'
    }
  }
}

// Helper function to get layout by ID
export const getLayoutById = (layoutId: EmailLayoutType): EmailLayoutTemplate => {
  return EMAIL_LAYOUTS[layoutId]
}

// A campaign's `layout` comes from crm-api, and the desktop app bundles its own
// copy of this registry — so a campaign saved with a template added AFTER that
// build (e.g. `spotlight_webinar` on a v0.1.108 app) arrives with an id this
// bundle has never heard of. Every lookup keyed on a stored layout must go
// through this guard; indexing EMAIL_LAYOUTS directly blanked the whole
// campaign page ("undefined is not an object (evaluating '…[o].name')").
export const isKnownLayout = (layoutId: unknown): layoutId is EmailLayoutType =>
  typeof layoutId === "string" && Object.prototype.hasOwnProperty.call(EMAIL_LAYOUTS, layoutId)

// Tenant information interface for template injection
export interface TenantInfo {
  name?: string
  logo_url?: string | null
  company_name?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
}

/**
 * Helper function to inject tenant information into email template
 * Replaces placeholders like {{TENANT_NAME}}, {{TENANT_LOGO}}, etc.
 * Also handles Mustache-style conditionals like {{#TENANT_LOGO}}...{{/TENANT_LOGO}}
 */
export const injectTenantInfo = (template: string, tenantInfo?: TenantInfo): string => {
  if (!tenantInfo) {
    // If no tenant info, remove all tenant placeholders and conditional blocks
    return template
      .replace(/\{\{#TENANT_\w+\}\}[\s\S]*?\{\{\/TENANT_\w+\}\}/g, '')
      .replace(/\{\{\^TENANT_\w+\}\}[\s\S]*?\{\{\/TENANT_\w+\}\}/g, '')
      .replace(/\{\{TENANT_\w+\}\}/g, '')
      .replace(/\{\{MONTH\}\}/g, new Date().toLocaleString('en-US', { month: 'long' }))
      .replace(/\{\{YEAR\}\}/g, new Date().getFullYear().toString())
  }

  let result = template

  // Get current date for month and year placeholders
  const currentDate = new Date()
  const month = currentDate.toLocaleString('en-US', { month: 'long' })
  const year = currentDate.getFullYear().toString()

  // Replace simple placeholders
  result = result
    .replace(/\{\{TENANT_NAME\}\}/g, tenantInfo.name || tenantInfo.company_name || 'Your Company')
    .replace(/\{\{TENANT_LOGO\}\}/g, toAbsoluteUrl(tenantInfo.logo_url))
    .replace(/\{\{TENANT_ADDRESS\}\}/g, tenantInfo.address || '')
    .replace(/\{\{TENANT_CITY\}\}/g, tenantInfo.city || '')
    .replace(/\{\{TENANT_STATE\}\}/g, tenantInfo.state || '')
    .replace(/\{\{TENANT_POSTAL_CODE\}\}/g, tenantInfo.postal_code || '')
    .replace(/\{\{TENANT_COUNTRY\}\}/g, tenantInfo.country || '')
    .replace(/\{\{TENANT_PHONE\}\}/g, tenantInfo.phone || '')
    .replace(/\{\{TENANT_EMAIL\}\}/g, tenantInfo.email || '')
    .replace(/\{\{TENANT_WEBSITE\}\}/g, tenantInfo.website || '')
    .replace(/\{\{MONTH\}\}/g, month)
    .replace(/\{\{YEAR\}\}/g, year)

  // Resolve the TenantInfo value a `{{#TENANT_FOO}}` conditional tests on. The
  // placeholder suffix matches the field key for every field EXCEPT the logo:
  // `{{#TENANT_LOGO}}` must look at `logo_url` (reading the literal "logo" key
  // would always be undefined and silently drop the whole conditional block).
  const conditionalValue = (field: string): TenantInfo[keyof TenantInfo] => {
    const fieldName = field.replace('TENANT_', '').toLowerCase()
    if (fieldName === 'logo') return tenantInfo.logo_url
    return tenantInfo[fieldName as keyof TenantInfo]
  }

  // Handle conditional blocks - show content if value exists
  // Format: {{#FIELD}}content{{/FIELD}}
  const conditionalRegex = /\{\{#(TENANT_\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g
  result = result.replace(conditionalRegex, (_match, field, content) =>
    conditionalValue(field) ? content : '',
  )

  // Handle negative conditional blocks - show content if value doesn't exist
  // Format: {{^FIELD}}content{{/FIELD}}
  const negativeConditionalRegex = /\{\{\^(TENANT_\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g
  result = result.replace(negativeConditionalRegex, (_match, field, content) =>
    !conditionalValue(field) ? content : '',
  )

  return result
}
